import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { ShareNetwork, WhatsappLogo, LinkedinLogo, XLogo, FacebookLogo, LinkSimple, Check, ArrowRight } from "@phosphor-icons/react";
import { renderMarkdown } from "@/lib/markdown";
import PageLoader from "@/components/PageLoader";

function ShareBar({ url, title }) {
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent;
  const links = [
    { id: "whatsapp", icon: WhatsappLogo, label: "WhatsApp", href: `https://wa.me/?text=${enc(title + ", " + url)}` },
    { id: "linkedin", icon: LinkedinLogo, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { id: "x", icon: XLogo, label: "X", href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}` },
    { id: "facebook", icon: FacebookLogo, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
  ];
  const copy = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch { /* fall through to clipboard */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.warn("[Article] copy failed:", e);
    }
  };
  return (
    <div className="md-article-share" data-testid="article-share">
      <span className="md-article-share-label"><ShareNetwork size={18} weight="duotone" /> Share</span>
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <a
            key={l.id}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className="md-article-share-btn"
            aria-label={`Share on ${l.label}`}
            data-testid={`article-share-${l.id}`}
          >
            <Icon size={18} weight="fill" />
          </a>
        );
      })}
      <button
        type="button"
        onClick={copy}
        className="md-article-share-btn"
        aria-label="Copy link"
        data-testid="article-share-copy"
      >
        {copied ? <Check size={18} weight="bold" /> : <LinkSimple size={18} weight="bold" />}
      </button>
    </div>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  useEffect(() => {
    setA(null);
    setRelated([]);
    api.get(`/articles/${slug}`).then((r) => setA(r.data));
  }, [slug]);
  useEffect(() => {
    if (!a) return;
    const params = a.category ? { category: a.category } : {};
    api.get("/articles", { params }).then((r) => {
      const others = (r.data || []).filter((x) => x.slug !== a.slug);
      // Fallback: if same-category has nothing else, fetch all and pick latest other.
      if (others.length === 0 && a.category) {
        api.get("/articles").then((r2) => {
          setRelated((r2.data || []).filter((x) => x.slug !== a.slug).slice(0, 3));
        });
      } else {
        setRelated(others.slice(0, 3));
      }
    });
  }, [a]);
  const shareUrl = useMemo(() => (typeof window !== "undefined" ? window.location.href : ""), [a]);
  if (!a) return <Page><PageLoader /></Page>;
  return (
    <Page>
      <header className="md-article-hero" data-testid="article-hero">
        {a.cover_url && (
          <img src={a.cover_url} alt={a.title} className="md-article-hero-img" loading="eager" />
        )}
        <div className="md-article-hero-overlay" aria-hidden />
        <div className="md-container md-article-hero-content">
          {a.category && (
            <div className="md-article-hero-eyebrow" data-testid="article-hero-category">{a.category}</div>
          )}
          <h1 className="md-article-hero-title font-serif" data-testid="article-hero-title">
            {a.title}
          </h1>
          <p className="md-article-hero-meta" data-testid="article-hero-meta">
            By <span className="md-article-hero-author">{a.author}</span>
            {a.created_at && (
              <>
                <span className="md-article-hero-dot" aria-hidden>·</span>
                <time dateTime={a.created_at}>
                  {new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </time>
              </>
            )}
            {a.read_time && (
              <>
                <span className="md-article-hero-dot" aria-hidden>·</span>
                <span>{a.read_time}</span>
              </>
            )}
          </p>
        </div>
      </header>

      <article className="md-section">
        <div className="md-container max-w-3xl">
          <div className="text-lg leading-relaxed md-article-body" style={{ color: "var(--md-text-soft)" }} data-testid="article-body">
            {renderMarkdown(a.body)}
          </div>
          <ShareBar url={shareUrl} title={a.title} />
        </div>
      </article>

      {related.length > 0 && (
        <section className="md-section md-related-articles" data-testid="article-related-section">
          <div className="md-container">
            <div className="md-related-articles-head">
              <div className="md-label-eyebrow mb-2">Keep reading</div>
              <h2 className="text-3xl md:text-4xl font-serif">Related articles</h2>
            </div>
            <div className="md-related-articles-grid">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/articles/${r.slug}`}
                  className="md-related-article-card"
                  data-testid={`article-related-${r.slug}`}
                >
                  {r.cover_url && (
                    <div className="md-related-article-cover">
                      <img src={r.cover_url} alt={r.title} loading="lazy" />
                    </div>
                  )}
                  <div className="md-related-article-body">
                    {r.category && <div className="md-label-eyebrow mb-2">{r.category}</div>}
                    <h3 className="md-related-article-title font-serif">{r.title}</h3>
                    {r.read_time && (
                      <div className="md-related-article-meta">{r.read_time}</div>
                    )}
                    <span className="md-related-article-cta">
                      Read article <ArrowRight size={14} weight="bold" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Page>
  );
}
